import router from "./router";
import { useAppStoreWithOut } from "@/store/modules/app";
import type { RouteRecordRaw } from "vue-router";
import { useTitle } from "@/hooks/web/useTitle";
import { useNProgress } from "@/hooks/web/useNProgress";
import { usePermissionStoreWithOut } from "@/store/modules/permission";
import { usePageLoading } from "@/hooks/web/usePageLoading";
import { NO_REDIRECT_WHITE_LIST } from "@/constants";
import { useUserStoreWithOut } from "@/store/modules/user";

const { start, done } = useNProgress();

const { loadStart, loadDone } = usePageLoading();

const normalizePath = (path: string): string => {
  const normalized = (path || "/").replace(/\/+/g, "/");
  const withLeadingSlash = normalized.startsWith("/")
    ? normalized
    : `/${normalized}`;

  return withLeadingSlash.replace(/\/+$/, "") || "/";
};

const buildFullPath = (routePath: string, basePath = ""): string => {
  if (!routePath) {
    return normalizePath(basePath || "/");
  }

  if (routePath.startsWith("/")) {
    return normalizePath(routePath);
  }

  return normalizePath(basePath ? `${basePath}/${routePath}` : routePath);
};

const hasPermissionRoute = (
  routes: any[],
  targetPath: string,
  basePath = "",
): boolean => {
  const normalizedTargetPath = normalizePath(targetPath);

  return routes.some((route) => {
    if (route.path === "/:path(.*)*") {
      return false;
    }

    const fullPath = buildFullPath(route.path, basePath);

    if (fullPath === normalizedTargetPath) {
      return true;
    }

    if (route.children && route.children.length > 0) {
      return hasPermissionRoute(route.children, normalizedTargetPath, fullPath);
    }

    return false;
  });
};

const findDeepestRoute = (routes: any[], basePath = ""): string | null => {
  for (const route of routes) {
    const fullPath = buildFullPath(route.path, basePath);

    if (route.children && route.children.length > 0) {
      const childPath = findDeepestRoute(route.children, fullPath);
      if (childPath) {
        return childPath;
      }
    } else if (
      route.component &&
      route.meta?.hidden !== true &&
      route.meta?.menu_url !== "0"
    ) {
      return fullPath;
    }
  }

  return null;
};

const hasRegisteredRoute = (targetPath: string): boolean => {
  const normalizedTargetPath = normalizePath(targetPath);

  return router.getRoutes().some((route) => {
    if (route.path === "/:path(.*)*") {
      return false;
    }

    return normalizePath(route.path) === normalizedTargetPath;
  });
};

router.beforeEach(async (to, from, next) => {
  start();
  loadStart();
  
  const permissionStore = usePermissionStoreWithOut();
  const appStore = useAppStoreWithOut();
  const userStore = useUserStoreWithOut();

  if (
    userStore.getUserInfo &&
    userStore.getToken &&
    userStore.getToken.trim() !== ""
  ) {
    if (to.path === "/login") {
      next({ path: "/" });
    } else {
      // 检查是否已经添加了路由
      if (
        permissionStore.getIsAddRouters &&
        permissionStore.getAddRouters.length > 0
      ) {
        next();
        return;
      }

      // 开发者可根据实际情况进行修改
      const roleRouters = userStore.getRoleRouters || [];

      // 是否使用动态路由
      if (appStore.getDynamicRouter) {
        if (appStore.serverDynamicRouter) {
          // 调用getAuth获取权限数据
          const { getAdminRoleApi } = await import("@/api/login");
          const res = await getAdminRoleApi({});

          if (res && res.data && res.data.menus) {
            await permissionStore.generateRoutes(
              "server",
              res.data.menus as AppCustomRouteRecordRaw[],
            );
            userStore.setAuthInfo(res.data);
          } else {
            await permissionStore.generateRoutes(
              "server",
              roleRouters as AppCustomRouteRecordRaw[],
            );
          }
        } else {
          await permissionStore.generateRoutes(
            "frontEnd",
            roleRouters as string[],
          );
        }
      } else {
        await permissionStore.generateRoutes("static");
      }

      permissionStore.getAddRouters.forEach((route) => {
        router.addRoute(route as unknown as RouteRecordRaw); // 动态添加可访问路由表
      });

      // 设置路由已添加标志
      permissionStore.setIsAddRouters(true);

      const addRouters = permissionStore.getAddRouters;
      const defaultPath = findDeepestRoute(addRouters);

      // 访问根路径、父菜单，或登录后命中了当前账号无权访问的旧 redirect 时，
      // 统一回退到第一个最深可用页面。
      const shouldRedirect =
        to.path === "/" ||
        to.path === "/dashboard" ||
        (to.matched.length > 0 &&
          to.matched[to.matched.length - 1].meta?.menu_url === "0") ||
        (from.path === "/login" &&
          to.path !== "/404" &&
          !hasPermissionRoute(addRouters, to.path) &&
          !hasRegisteredRoute(to.path));

      if (shouldRedirect) {
        if (defaultPath) {
          next({ path: normalizePath(defaultPath), replace: true });
          return;
        } else {
          // 如果没有找到任何可用路由，跳转到 404
          next({ path: "/404", replace: true });
          return;
        }
      }

      //

      // 重新解析当前路由，确保动态路由生效
      next({ ...to, replace: true });
    }
  } else {
    if (NO_REDIRECT_WHITE_LIST.indexOf(to.path) !== -1) {
      next();
    } else {
      next(`/login?redirect=${to.path}`); // 否则全部重定向到登录页
    }
  }
});

router.afterEach((to) => {
  useTitle(to?.meta?.title as string);
  done(); // 结束Progress
  loadDone();
});
